/** @jsx jsx */
import type { SerializedStyles } from '@emotion/react';
import { jsx } from '@emotion/react';

import { useCallback, useEffect } from 'react';

import type { AiCoach_Mode as AiCoachMode } from '__generated__/graphql-types';
import type { ApolloError } from 'apollo-client';

import { useLocation } from 'js/lib/useRouter';

import type { CourseItem, Product } from '@coursera/event-pulse-types';

import { CoachChat } from 'bundles/ai-coach-platform';
import { sendMessage as sendMessagePlatform } from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import { useChatHistory } from 'bundles/ai-course-coach/components/ConversationHistory';
import EmptyState from 'bundles/ai-course-coach/components/HomeView/EmptyState';
import { getErrorMessageText } from 'bundles/ai-course-coach/components/constants';
import store from 'bundles/ai-course-coach/store';
import {
  addResponseMessage,
  dropMessages,
  enableMsgLoader,
  setChatSessionId,
  setChatSessionStorageKey,
} from 'bundles/ai-course-coach/store/dispatcher';
import createChatSessionStorageKey from 'bundles/ai-course-coach/utils/createChatSessionStorageKey';
import { persistantChatSessionId } from 'bundles/ai-course-coach/utils/persistantChatSessionId';
import { isCoachAudioEnabled } from 'bundles/ai-course-coach/utils/productFeatureFlags';
import { captureMessageInSentry } from 'bundles/ai-course-coach/utils/sentryUtils';
import type { HomeView as HomeViewType } from 'bundles/ai-course-coach/utils/types';
import { useTracker } from 'bundles/page/lib/event-pulse/react';

export type Props = {
  itemId?: string;
  courseId?: string;
  mode: AiCoachMode;
  homeView?: HomeViewType;
  recommendedActionsEnabled?: boolean;
  clipRecommendationsEnabled?: boolean;
  chatSessionKey?: string;
  onClose?: () => void;
  cssOverrides?: SerializedStyles;
  disableSpeechToText?: boolean;
  speechToTextDisabledTooltip?: string;
};

function handleError(apolloError: ApolloError) {
  enableMsgLoader(false);
  addResponseMessage({ text: getErrorMessageText() });
  if (apolloError.graphQLErrors && apolloError.graphQLErrors?.length > 0) {
    const graphQLError = apolloError.graphQLErrors[0];
    captureMessageInSentry({ error: graphQLError, source: 'sendMessage' });
  }
}

/**
 * Main component for the Coach chat widget that is shown on several of our pages for
 * learners to interact with the Coach AI.
 */
const CoachChatEmbedded = ({
  itemId,
  courseId,
  recommendedActionsEnabled = false,
  clipRecommendationsEnabled = false,
  mode,
  homeView,
  chatSessionKey = '',
  onClose,
  cssOverrides,
  disableSpeechToText = false,
  speechToTextDisabledTooltip = '',
}: Props) => {
  const location = useLocation();
  const { sessionState } = store.getState();
  const { chatSessionId: chatSessionIdState } = sessionState;

  const { refreshHistory } =
    useChatHistory({
      chatSessionId: chatSessionIdState,
      historyLimit: 5,
      clipRecommendationsEnabled,
      recommendedActionsEnabled,
    }) || {};

  const track = useTracker();

  const getSessionStorageKey = useCallback<() => $TSFixMe>(() => {
    const locationString = location.pathname + location.search + location.query;
    const sessionStorageKeyData = chatSessionKey || locationString;
    return createChatSessionStorageKey(sessionStorageKeyData);
  }, [location, chatSessionKey]);

  useEffect(() => {
    const sessionStorageKey = getSessionStorageKey();
    const sanitizedChatSessionId = persistantChatSessionId.get(sessionStorageKey);
    setChatSessionStorageKey(sessionStorageKey);

    setChatSessionId(sanitizedChatSessionId);
    dropMessages();

    if (sanitizedChatSessionId) {
      refreshHistory?.(sanitizedChatSessionId);
    } else {
      dropMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]); // TODO: adding `refreshHistory` as dependency results in infinite loop, needs more investigation

  const handleClose = () => {
    onClose?.();

    // used to notify ExternalCoachLauncher when Coach is closed
    document.body.dispatchEvent(new CustomEvent('coachChatClosed'));

    track('interact_coach', {
      coachInteractionType: 'close_coach_tool',
      coach: {
        mode,
        location: 'toolbar',
      },
      ...(courseId && {
        product: {
          type: 'course',
          id: courseId,
        } as Product,
      }),
      ...(itemId && {
        courseItem: {
          id: itemId,
        } as CourseItem,
      }),
    });
  };

  const contextConfig = { courseraContext: { courseId, itemId } };
  return (
    <div data-testid="coursera-coach-widget">
      <CoachChat
        enableRichInputField={isCoachAudioEnabled()}
        additionalStyles={cssOverrides}
        showSettings={true}
        trackingData={{
          mode,
          location: 'toolbar',
          courseId,
          itemId,
        }}
        emptyState={
          <EmptyState
            sendMessage={(message: string, action: string) => {
              sendMessagePlatform({
                input: {
                  message,
                  config: {
                    coachActionId: action,
                    coachModeId: mode,
                  },
                  ...contextConfig,
                },
              }).catch(handleError);
            }}
            quickMessages={homeView?.quickMessages}
            title={homeView?.title}
            subtitle={homeView?.subtitle}
          />
        }
        mode={mode}
        contextConfig={contextConfig}
        onClose={handleClose}
        disableSpeechToText={disableSpeechToText}
        speechToTextDisabledTooltip={speechToTextDisabledTooltip}
      />
    </div>
  );
};

CoachChatEmbedded.displayName = 'CoachChatEmbedded';

export default CoachChatEmbedded;
